/**
 * The faction hostility matrix (M4 spec §10.5, plan I1). M1 treated "a different team" as "an enemy"; with nine
 * factions that would make every trader attack every other trader. Every **enemy** test (targeting, AI
 * perception, torpedo pursuit, HUD flags, enemy counts, hostile-nearby checks) asks `isHostile` instead. The
 * **friendly-fire** test stays as it was (projectile and torpedo damage skip only the shooter's own team).
 *
 * - `HOSTILITY` is a 16 × 16 `Uint8Array` indexed `a * 16 + b`. A cell is hostile when it is non-zero: bit 1 is
 *   the standing relation (`setHostile`, later written by M5 wars, wanted status and standings), bit 2 a grudge.
 * - Defaults (`resetHostility`, M1's behaviour for teams 0 and 1): the raider team is hostile to every team but
 *   itself, so the player is hostile to raiders; everyone else is neutral.
 * - The player corporation (M6 plan P22): its strategic slot flies as `PLAYER_CORP_TEAM_ID`, the player's own team
 *   (corp.json `slot.teamId` 0). Corporation ships are therefore never hostile to the player or each other (a team is
 *   never hostile to itself), friendly fire skips them, and a player hit on one starts no grudge. Relations between
 *   the corporation and the factions arrive through `setFromRelations` like everyone else's (the Charter writer maps
 *   the corporation's skirmish and war pairs onto this team).
 * - Grudges: when the **player** damages a ship of a team it is not standing-hostile to, that team and the
 *   player's team become mutually hostile for `grudgeS` seconds (local.json, 120 s), refreshed on each hit. Stray
 *   AI fire between neutral teams creates none. `stepGrudges` expires them once per sim tick.
 *
 * Module state, like the ECS component arrays. Everything here is allocation-free (D-014).
 */
import localJson from '../data/strategic/local.json'
import { localConfigSchema } from '../strategic/schemas'
import { Team } from '../world/components'

/** Team slots in the matrix (M1 `Team.id` values 0..15). Teams outside it are never hostile. */
export const TEAM_SLOTS = 16
/** The player's team (sim.ts `PLAYER_TEAM`; kept local so combat and HUD code do not pull in Rapier). */
export const PLAYER_TEAM_ID = 0
/** The player corporation's team (M6 plan P22; corp.json `slot.teamId`): the player's own team, so always friendly. */
export const PLAYER_CORP_TEAM_ID = PLAYER_TEAM_ID
/** The raider clans' team (sim.ts `RAIDER_TEAM`, strategic factions.json `raider.teamId`). */
export const RAIDER_TEAM_ID = 1

const STANDING = 1
const GRUDGE = 2

/** The matrix: `HOSTILITY[a * TEAM_SLOTS + b] !== 0` means team `a` treats team `b` as an enemy. */
export const HOSTILITY = new Uint8Array(TEAM_SLOTS * TEAM_SLOTS)
/** Sim time (s) at which each cell's grudge ends. Meaningful only while the cell's grudge bit is set. */
const grudgeUntil = new Float64Array(TEAM_SLOTS * TEAM_SLOTS)

/** Tuning (local.json `grudgeS`); mutable so the dev panel can bind it. */
export const HOSTILITY_TUNING = { grudgeS: localConfigSchema.parse(localJson).grudgeS }

/** Grudge bookkeeping: live grudge cells, the player entity the damage hook recognises, and the last step time. */
const state = { grudges: 0, playerEid: -1, lastNow: 0 }

function inRange(team: number): boolean {
  return team >= 0 && team < TEAM_SLOTS
}

/** True when team `a` treats team `b` as an enemy. A team is never hostile to itself. */
export function isHostile(a: number, b: number): boolean {
  return a !== b && inRange(a) && inRange(b) && HOSTILITY[a * TEAM_SLOTS + b] !== 0
}

/** True when `team` fights on the player's side: the player's team, which the corporation's ships share (P22). */
export function isPlayerSide(team: number): boolean {
  return team === PLAYER_TEAM_ID
}

/** True when ship `a`'s team treats ship `b`'s team as an enemy (the entity form of `isHostile`). */
export function isHostileShip(a: number, b: number): boolean {
  return isHostile(Team.id[a], Team.id[b])
}

/** Sets or clears the standing hostility between teams `a` and `b`, both ways. Grudges are kept. */
export function setHostile(a: number, b: number, on: boolean): void {
  if (a === b || !inRange(a) || !inRange(b)) return
  const ab = a * TEAM_SLOTS + b
  const ba = b * TEAM_SLOTS + a
  if (on) {
    HOSTILITY[ab] |= STANDING
    HOSTILITY[ba] |= STANDING
  } else {
    HOSTILITY[ab] &= ~STANDING
    HOSTILITY[ba] &= ~STANDING
  }
}

/** True when team `a`'s standing relation (not a grudge) makes team `b` an enemy. The crime test (M5 plan C14). */
export function isStandingHostile(a: number, b: number): boolean {
  return a !== b && inRange(a) && inRange(b) && (HOSTILITY[a * TEAM_SLOTS + b] & STANDING) !== 0
}

/**
 * M5 (plan C14, spec §14.1): rewrites every standing cell from `standing` (`TEAM_SLOTS²` cells indexed `a * 16 + b`,
 * non-zero = hostile), as the Charter's relations, wanted levels and standings say. Grudges are kept. The
 * diagonal is ignored (a team is never hostile to itself). Allocation-free.
 */
export function setFromRelations(standing: Uint8Array): void {
  for (let a = 0; a < TEAM_SLOTS; a++) {
    for (let b = 0; b < TEAM_SLOTS; b++) {
      const cell = a * TEAM_SLOTS + b
      if (a !== b && standing[cell] !== 0) HOSTILITY[cell] |= STANDING
      else HOSTILITY[cell] &= ~STANDING
    }
  }
}

/** Restores the M1 defaults (raiders against everyone, everyone else neutral) and clears every grudge. */
export function resetHostility(): void {
  HOSTILITY.fill(0)
  grudgeUntil.fill(0)
  state.grudges = 0
  state.lastNow = 0
  for (let t = 0; t < TEAM_SLOTS; t++) setHostile(RAIDER_TEAM_ID, t, true)
}

function setGrudge(cell: number, until: number): void {
  if ((HOSTILITY[cell] & GRUDGE) === 0) {
    HOSTILITY[cell] |= GRUDGE
    state.grudges++
  }
  grudgeUntil[cell] = until
}

/** Makes teams `a` and `b` mutually hostile until `now + grudgeS`, or refreshes a running grudge. */
export function addGrudge(a: number, b: number, now: number): void {
  if (a === b || !inRange(a) || !inRange(b)) return
  const until = now + HOSTILITY_TUNING.grudgeS
  setGrudge(a * TEAM_SLOTS + b, until)
  setGrudge(b * TEAM_SLOTS + a, until)
}

/** Seconds left on the grudge between teams `a` and `b` at `now` (0 when there is none). */
export function grudgeRemaining(a: number, b: number, now: number): number {
  if (!inRange(a) || !inRange(b)) return 0
  const cell = a * TEAM_SLOTS + b
  if ((HOSTILITY[cell] & GRUDGE) === 0) return 0
  const left = grudgeUntil[cell] - now
  return left > 0 ? left : 0
}

function clearGrudges(): void {
  for (let cell = 0; cell < HOSTILITY.length; cell++) HOSTILITY[cell] &= ~GRUDGE
  state.grudges = 0
}

/**
 * Once per sim tick: records the player entity for the damage hook and ends grudges whose time is up. A clock
 * that ran backwards (a new sim in the same page) clears every grudge.
 */
export function stepGrudges(now: number, playerEid: number): void {
  state.playerEid = playerEid
  if (now < state.lastNow) clearGrudges()
  state.lastNow = now
  if (state.grudges === 0) return
  for (let cell = 0; cell < HOSTILITY.length; cell++) {
    if ((HOSTILITY[cell] & GRUDGE) !== 0 && grudgeUntil[cell] <= now) {
      HOSTILITY[cell] &= ~GRUDGE
      state.grudges--
    }
  }
}

/**
 * The damage hook (projectile and torpedo hits that dealt damage): when the attacker is the player and the
 * victim's team is not standing-hostile to the player's, starts or refreshes a grudge. AI fire never does.
 */
export function notePlayerDamage(attacker: number, victim: number, now: number): void {
  if (attacker < 0 || attacker !== state.playerEid || victim < 0) return
  const a = Team.id[attacker]
  const b = Team.id[victim]
  if (a === b || !inRange(a) || !inRange(b) || (HOSTILITY[a * TEAM_SLOTS + b] & STANDING) !== 0) return
  addGrudge(a, b, now)
}

resetHostility()
